import { useEffect, useRef } from 'react';

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-in').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('opacity-100', 'translate-y-0');
                el.classList.remove('opacity-0', 'translate-y-8');
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden bg-background-50"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/60 via-background-50 to-accent-50/40"></div>
      <div className="absolute top-0 right-0 w-[60%] h-full opacity-[0.06]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,var(--primary-500),transparent_70%)]"></div>
      </div>

      <div className="relative w-full px-4 md:px-6 py-20 md:py-24">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1 w-full text-center lg:text-left">
            <div className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out">
              <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-5">
                🔥 全网热播 · 每日更新
              </span>
            </div>

            <h1 className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 font-heading leading-tight mb-4">
              7777影院
              <span className="block text-primary-600 mt-1 text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                高清免费影视在线平台
              </span>
            </h1>

            <p className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out text-lg md:text-xl text-foreground-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 mt-3">
              精彩，从这里开始
            </p>

            <div className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out mt-6">
              <p className="text-foreground-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                7777影院汇聚海量高清电影、电视剧大全、热门综艺与动漫番剧，全网资源免费在线观看。
                采用高速CDN分发技术，播放流畅不卡顿，告别缓冲烦恼。每日同步更新最新剧集与院线大片，
                无论你是追剧达人还是电影爱好者，这里都能满足你的观影需求。
              </p>
            </div>

            <div className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-7">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                <span>日更新 200+ 部影视资源</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                <span>全高清 &amp; 4K 画质支持</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
                <span>全网CDN极速秒播</span>
              </div>
            </div>

            <div className="animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out flex flex-wrap items-center justify-center lg:justify-start gap-4 mt-8">
              <a
                href="#updates"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#updates')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors text-base whitespace-nowrap cursor-pointer"
              >
                开始观影
              </a>
              <a
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-3.5 border-2 border-foreground-200 text-foreground-700 font-semibold rounded-lg hover:border-primary-400 hover:text-primary-600 transition-colors text-base whitespace-nowrap cursor-pointer"
              >
                了解优势
              </a>
            </div>
          </div>

          <div className="flex-1 w-full animate-in opacity-0 translate-y-8 transition-all duration-600 ease-out">
            <div className="relative mx-auto max-w-[500px]">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-400/15 to-accent-400/15 rounded-2xl blur-2xl"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Artistic%20cinematic%20composition%20with%20warm%20theater%20lighting%2C%20film%20reel%20and%20vintage%20movie%20posters%20collage%20arrangement%2C%20rich%20amber%20and%20crimson%20tones%2C%20retro%20cinema%20aesthetic%20with%20modern%20twist%2C%20dramatic%20spotlight%20effect%2C%20celluloid%20film%20strip%20texture%2C%20dark%20theater%20atmosphere%20with%20warm%20glowing%20accents%2C%20creative%20and%20artistic%20movie%20culture%20visual&width=1000&height=1250&seq=hero-movie-2026&orientation=portrait"
                  alt="7777影院 - 高清免费影视在线平台"
                  className="w-full h-auto object-cover"
                  width="500"
                  height="625"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent-500/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary-500/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}